import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { map, Observable } from 'rxjs';
import { Environment } from 'src/environments/environment.model';

import { signalStoreCustomerRequestOptions } from '../../shared/interceptors/signal-store-customer-context';
import { GetVirusMonitorAdvisoriesApiResponse, GetVirusMonitorAdvisoryApiResponse } from '../models/api-models';
import { mapGetVirusMonitorAdvisoriesResponse, mapGetVirusMonitorAdvisoryResponse } from '../models/mappers';
import { VirusMonitorRegistration } from '../models/models';
import { VirusSprayCategory } from '@app/shared/components/disease-overview-table.component/models/models';

@Injectable({
  providedIn: 'root',
})
export class VirusMonitorService {
  private readonly http = inject(HttpClient);
  private readonly env = inject<Environment>(EnvironmentInjector);

  private readonly baseUrl = `${this.env.APIM_BASE_URL}/arable-disease-integration-service/virus-monitor`;
  private readonly cropFieldsEndpoint = 'crop-fields';

  public getAdvisory(
    cropFieldId: number,
    cropYear: number,
    customerId?: number,
  ): Observable<VirusMonitorRegistration> {
    const url = `${this.baseUrl}/${cropFieldId}`;
    return this.http
      .get<GetVirusMonitorAdvisoryApiResponse>(url, signalStoreCustomerRequestOptions(customerId))
      .pipe(map((response) => mapGetVirusMonitorAdvisoryResponse(response, cropYear)));
  }

  public getAdvisories(
    cropYear: number,
    startDate?: Date,
    endDate?: Date,
    customerId?: number,
  ): Observable<VirusMonitorRegistration[]> {
    const url = `${this.baseUrl}/advisories`;

    let params = new HttpParams().set('cropYear', cropYear.toString());

    if (startDate) {
      params = params.set('fromDate', this.formatDate(startDate));
    }

    if (endDate) {
      params = params.set('untilDate', this.formatDate(endDate));
    }

    return this.http
      .get<GetVirusMonitorAdvisoriesApiResponse>(url, { params, ...signalStoreCustomerRequestOptions(customerId) })
      .pipe(map((response) => mapGetVirusMonitorAdvisoriesResponse(response, cropYear)));
  }

  public recalculateAdvisories(cropFieldIds: number[]): Observable<void> {
    const url = `${this.baseUrl}/${this.cropFieldsEndpoint}/recalculate`;

    let params = new HttpParams();
    cropFieldIds.forEach((id) => {
      params = params.append('cropFieldIds', id.toString());
    });

    return this.http.post<void>(url, null, { params });
  }

  public updateDeadFoliageDate(cropFieldId: number, date: Date): Observable<void> {
    const url = `${this.baseUrl}/${cropFieldId}/dead-foliage-date`;
    return this.http.patch<void>(url, { deadFoliageDate: this.formatDate(date) });
  }

  public updateSprayThreshold(cropFieldId: number, sprayThresholdPercentage: number): Observable<void> {
    const url = `${this.baseUrl}/${cropFieldId}/spray-threshold`;
    return this.http.patch<void>(url, { sprayThresholdPercentage });

  }

  public updateProtectionTypes(cropFieldId: number, selectedProtectionTypes: VirusSprayCategory[]): Observable<void> {
    const url = `${this.baseUrl}/${cropFieldId}/protection-types`;
    return this.http.patch<void>(url, { selectedProtectionTypes });
  }

  private formatDate(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  }
}
